import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  Link,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { Add, Edit, ImageOutlined as ImageOutlinedIcon } from "@material-ui/icons";
import { useDispatch } from "react-redux";
import DialogTitle from "../../../components/dialog-title";
import customersService from "../../../services/customers.service";
import LoadingComponent from "../../../components/loading";
import { dateFormat } from "../../../helpers/date-helpers";
import { phoneFormat } from "../../../helpers";
import { openImage } from "../../../redux/app";
import { useErrorHandler } from "../../../hooks";
import PrescriptionModal from "./prescription/prescription-modal";

const emptyPrescriptionForm = () => ({
  title: "",
  image: null,
  imageUrl: "",
  doctorName: "",
  rightEyeSph: "",
  rightEyeCyl: "",
  rightEyeAxe: "",
  leftEyeSph: "",
  leftEyeCyl: "",
  leftEyeAxe: "",
  pd: "",
  add: "",
  description: "",
});

function prescriptionPayload(form) {
  const payload = {
    title: form.title,
    doctorName: form.doctorName,
    rightEyeSph: form.rightEyeSph,
    rightEyeCyl: form.rightEyeCyl,
    rightEyeAxe: form.rightEyeAxe,
    leftEyeSph: form.leftEyeSph,
    leftEyeCyl: form.leftEyeCyl,
    leftEyeAxe: form.leftEyeAxe,
    pd: form.pd,
    add: form.add,
    description: form.description,
  };

  if (form.image) payload.image = form.image;
  return payload;
}

export default function InfoModalContent({ close, data }) {
  const dispatch = useDispatch();
  const errorHandler = useErrorHandler();

  const [customer, setCustomer] = useState(data);
  const [info, setInfo] = useState();
  const [prescriptions, setPrescriptions] = useState([]);
  const [editingUser, setEditingUser] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [userForm, setUserForm] = useState({
    fullName: data.fullName || "",
    phoneNumber: data.phoneNumber || "",
    nationalCode: data.nationalCode || "",
    email: data.email || "",
    gender: data.gender || "",
    firstNameEnglish: data.firstNameEnglish || "",
    lastNameEnglish: data.lastNameEnglish || "",
  });
  const [prescriptionModal, setPrescriptionModal] = useState({
    open: false,
    mode: "create",
    prescriptionId: "",
  });
  const [prescriptionForm, setPrescriptionForm] = useState(emptyPrescriptionForm);
  const [prescriptionLoading, setPrescriptionLoading] = useState(false);

  const loadCustomer = () => {
    customersService.getCustomer(data.id).then(({ data: customerInfo, error }) => {
      if (error) return errorHandler(error);
      setInfo(customerInfo);
      setCustomer((current) => ({ ...current, ...customerInfo }));
    });
  };

  const loadPrescriptions = () => {
    customersService
      .getCustomerPrescriptions(data.id)
      .then(({ data: items, error }) => {
        if (error) return errorHandler(error);
        setPrescriptions(items?.items || items || []);
      });
  };

  useEffect(() => {
    loadCustomer();
    loadPrescriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.id]);

  const updateUserField = (event) => {
    const { name, value } = event.target;
    setUserForm((current) => ({ ...current, [name]: value }));
  };

  const saveUser = async () => {
    const model = {
      ...userForm,
      gender: userForm.gender || null,
      firstNameEnglish: userForm.firstNameEnglish || null,
      lastNameEnglish: userForm.lastNameEnglish || null,
    };

    setSavingUser(true);
    const { data: updatedUser, error } = await customersService.editCustomer(
      data.id,
      model
    );
    setSavingUser(false);
    if (error) return errorHandler(error);

    setCustomer((current) => ({ ...current, ...updatedUser, ...model }));
    setInfo((current) => ({ ...current, ...updatedUser, ...model }));
    setEditingUser(false);
  };

  const openCreatePrescription = () => {
    setPrescriptionForm(emptyPrescriptionForm());
    setPrescriptionModal({ open: true, mode: "create", prescriptionId: "" });
  };

  const openEditPrescription = (prescription) => {
    setPrescriptionForm({
      ...emptyPrescriptionForm(),
      title: prescription.title || "",
      imageUrl: prescription.image || "",
      doctorName: prescription.doctorName || "",
      rightEyeSph: prescription.rightEyeSph || "",
      rightEyeCyl: prescription.rightEyeCyl || "",
      rightEyeAxe: prescription.rightEyeAxe || "",
      leftEyeSph: prescription.leftEyeSph || "",
      leftEyeCyl: prescription.leftEyeCyl || "",
      leftEyeAxe: prescription.leftEyeAxe || "",
      pd: prescription.pd || "",
      add: prescription.add || "",
      description: prescription.description || "",
    });
    setPrescriptionModal({
      open: true,
      mode: "edit",
      prescriptionId: prescription.id,
    });
  };

  const closePrescriptionModal = () => {
    if (prescriptionLoading) return;
    setPrescriptionModal({ open: false, mode: "create", prescriptionId: "" });
    setPrescriptionForm(emptyPrescriptionForm());
  };

  const savePrescription = async () => {
    const isEdit = prescriptionModal.mode === "edit";
    if (!isEdit && !prescriptionForm.image) return;

    setPrescriptionLoading(true);
    const result = isEdit
      ? await customersService.updateCustomerPrescription(
          data.id,
          prescriptionModal.prescriptionId,
          prescriptionPayload(prescriptionForm)
        )
      : await customersService.uploadCustomerPrescription(
          data.id,
          prescriptionPayload(prescriptionForm)
        );
    setPrescriptionLoading(false);

    if (result.error) return errorHandler(result.error);
    closePrescriptionModal();
    loadCustomer();
    loadPrescriptions();
  };

  const latestPrescription = useMemo(
    () => prescriptions.find((item) => item.id === info?.prescription?.id) || info?.prescription,
    [info, prescriptions]
  );

  return (
    <>
      <DialogTitle onClose={() => close(true)}>کاربر</DialogTitle>
      <Divider />
      <DialogContent className="py-3">
        <Paper className="p-2 mb-3" variant="outlined">
          <div className="d-flex space-between align-center mb-2">
            <Typography variant="h6">اطلاعات مشتری</Typography>
            {!editingUser ? (
              <Button
                size="small"
                color="primary"
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => setEditingUser(true)}
              >
                ویرایش
              </Button>
            ) : null}
          </div>
          <Divider className="mb-2" />
          {editingUser ? (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="fullName"
                    label="نام"
                    value={userForm.fullName}
                    onChange={updateUserField}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="phoneNumber"
                    label="شماره تلفن"
                    value={userForm.phoneNumber}
                    onChange={updateUserField}
                    inputProps={{ dir: "ltr" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="nationalCode"
                    label="کد ملی"
                    value={userForm.nationalCode}
                    onChange={updateUserField}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="email"
                    label="ایمیل"
                    value={userForm.email}
                    onChange={updateUserField}
                    inputProps={{ dir: "ltr" }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    name="gender"
                    label="جنسیت"
                    value={userForm.gender}
                    onChange={updateUserField}
                  >
                    <MenuItem value="">-</MenuItem>
                    <MenuItem value="male">مرد</MenuItem>
                    <MenuItem value="female">زن</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="firstNameEnglish"
                    label="نام انگلیسی"
                    value={userForm.firstNameEnglish}
                    onChange={updateUserField}
                    inputProps={{ dir: "ltr", maxLength: 31 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="lastNameEnglish"
                    label="نام خانوادگی انگلیسی"
                    value={userForm.lastNameEnglish}
                    onChange={updateUserField}
                    inputProps={{ dir: "ltr", maxLength: 31 }}
                  />
                </Grid>
              </Grid>
              <DialogActions>
                <Button onClick={() => setEditingUser(false)} disabled={savingUser}>
                  انصراف
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={saveUser}
                  disabled={savingUser}
                >
                  ذخیره
                </Button>
              </DialogActions>
            </>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography>نام: {customer.fullName}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  شماره تلفن:{" "}
                  <span className="en">{phoneFormat(customer.phoneNumber)}</span>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography>کد ملی: {customer.nationalCode}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography>ایمیل: {customer.email}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  جنسیت:{" "}
                  {customer.gender === "male"
                    ? "مرد"
                    : customer.gender === "female"
                    ? "زن"
                    : "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  نام انگلیسی:{" "}
                  <span className="en">{customer.firstNameEnglish || "-"}</span>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  نام خانوادگی انگلیسی:{" "}
                  <span className="en">{customer.lastNameEnglish || "-"}</span>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography>تاریخ عضویت: {dateFormat(customer.createDate)}</Typography>
              </Grid>
            </Grid>
          )}
        </Paper>

        {!info ? (
          <LoadingComponent />
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography>بیمه پایه: {info.basicInsurance?.name}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography>
                بیمه تکمیلی: {info.supplementalInsurance?.name}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography className="d-flex">
                آخرین نسخه:{" "}
                {latestPrescription?.image ? (
                  <>
                    <ImageOutlinedIcon
                      onClick={() => dispatch(openImage(latestPrescription.image))}
                    />
                    {latestPrescription.isInvalid && "(رد شده)"}
                  </>
                ) : (
                  ""
                )}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography className="d-flex">
                آخرین بروزرسانی نسخه:{" "}
                {latestPrescription?.updateDate
                  ? dateFormat(latestPrescription.updateDate)
                  : ""}
              </Typography>
            </Grid>
            {Boolean(latestPrescription?.description) && (
              <Grid item xs={12}>
                <Typography className="d-flex">
                  توضیحات نسخه: {latestPrescription.description}
                </Typography>
              </Grid>
            )}
          </Grid>
        )}

        {info && (
          <Paper className="p-2 mt-4" variant="outlined">
            <Typography variant="h6" className="mb-2">
              آدرس‌ها
            </Typography>
            <Divider className="mb-2" />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>عنوان</TableCell>
                  <TableCell>استان</TableCell>
                  <TableCell>شهر</TableCell>
                  <TableCell>محله</TableCell>
                  <TableCell>آدرس</TableCell>
                  <TableCell>کد پستی</TableCell>
                  <TableCell>موقعیت</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {info.addresses?.length ? (
                  info.addresses.map((address) => (
                    <TableRow key={address.id}>
                      <TableCell>{address.name || "-"}</TableCell>
                      <TableCell>{address.province?.name || "-"}</TableCell>
                      <TableCell>{address.city?.name || "-"}</TableCell>
                      <TableCell>{address.neighborhood || "-"}</TableCell>
                      <TableCell>{address.postalAddress || "-"}</TableCell>
                      <TableCell>{address.postalCode || "-"}</TableCell>
                      <TableCell>
                        {address.location ? (
                          <Link
                            target="_blank"
                            rel="noreferrer"
                            href={`https://www.google.com/maps/search/?api=1&query=${address.location}`}
                          >
                            {address.location}
                          </Link>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      آدرسی ثبت نشده است.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        )}

        <Paper className="p-2 mt-4" variant="outlined">
          <div className="d-flex space-between align-center mb-2">
            <Typography variant="h6">نسخه‌ها</Typography>
            <Button
              size="small"
              color="primary"
              variant="contained"
              startIcon={<Add />}
              onClick={openCreatePrescription}
            >
              نسخه جدید
            </Button>
          </div>
          <Divider className="mb-2" />
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>عنوان</TableCell>
                <TableCell>پزشک</TableCell>
                <TableCell>تاریخ ایجاد</TableCell>
                <TableCell>آخرین ویرایش</TableCell>
                <TableCell align="center">تصویر</TableCell>
                <TableCell align="center">عملیات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {prescriptions.length ? (
                prescriptions.map((prescription) => (
                  <TableRow key={prescription.id}>
                    <TableCell>{prescription.title || "-"}</TableCell>
                    <TableCell>{prescription.doctorName || "-"}</TableCell>
                    <TableCell>{dateFormat(prescription.createDate)}</TableCell>
                    <TableCell>{dateFormat(prescription.updateDate)}</TableCell>
                    <TableCell align="center">
                      {prescription.image ? (
                        <ImageOutlinedIcon
                          style={{ cursor: "pointer" }}
                          onClick={() => dispatch(openImage(prescription.image))}
                        />
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        color="primary"
                        onClick={() => openEditPrescription(prescription)}
                      >
                        ویرایش
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    نسخه‌ای ثبت نشده است.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </DialogContent>

      <PrescriptionModal
        open={prescriptionModal.open}
        form={prescriptionForm}
        loading={prescriptionLoading}
        title={
          prescriptionModal.mode === "edit"
            ? "مشاهده و ویرایش نسخه"
            : "ثبت نسخه جدید"
        }
        submitLabel={
          prescriptionModal.mode === "edit" ? "ذخیره تغییرات" : "ثبت نسخه"
        }
        onClose={closePrescriptionModal}
        onChange={setPrescriptionForm}
        onSubmit={savePrescription}
      />
    </>
  );
}
